import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Boxes,
  CheckCircle2,
  ClipboardList,
  Plus,
  Rocket,
  ShieldCheck,
} from "lucide-react";
import toast from "react-hot-toast";
import LoadingSpinner from "../components/LoadingSpinner";
import MessageCard from "../components/MessageCard";
import OnboardingRequestDetailModal, {
  OnboardingStatusBadge,
} from "../components/OnboardingRequestDetailModal";
import OnboardingRequestForm from "../components/OnboardingRequestForm";
import { featureRequestStatusLabel } from "../constants/featureRequests";
import {
  ONBOARDING_REQUEST_TYPES,
  onboardingTypeBySlug,
} from "../constants/onboardingRequests";
import { apiService, extractApiError } from "../services/api";
import type { OnboardingRequest, OnboardingRequestType } from "../types";
import {
  countOnboardingRequests,
  editedSinceStatusChange,
  filterOnboardingRequests,
  ONBOARDING_FILTERS,
  ONBOARDING_STATUSES,
  type OnboardingFilter,
} from "../utils/onboardingRequests";

type OnboardingTab = "requests" | "new";

const TABS: { id: OnboardingTab; label: string; icon: typeof Plus }[] = [
  { id: "requests", label: "Requests", icon: ClipboardList },
  { id: "new", label: "New request", icon: Plus },
];

const TYPE_ICONS: Record<string, typeof Rocket> = {
  "k8s-namespace": Boxes,
  "dependency-track": ShieldCheck,
};

function typeIcon(slug: string) {
  return TYPE_ICONS[slug] ?? Rocket;
}

function splitTitle(title: string): { kind: string; subject: string } {
  const at = title.indexOf(": ");
  if (at === -1) return { kind: "", subject: title };
  return { kind: title.slice(0, at), subject: title.slice(at + 2) };
}

function DateTimeCell({ value }: { value: string | null }) {
  if (!value) {
    return <span className="text-gray-400 dark:text-gray-500">–</span>;
  }
  const date = new Date(value);
  return (
    <>
      <div>{date.toLocaleDateString()}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400">
        {date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
      </div>
    </>
  );
}

interface OnboardingListData {
  items: OnboardingRequest[];
  isAdmin: boolean;
}

export default function OnboardingRequestsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { typeSlug } = useParams<{ typeSlug: string }>();
  const activeTab: OnboardingTab = location.pathname.startsWith(
    "/onboarding/new",
  )
    ? "new"
    : "requests";
  const selectedType: OnboardingRequestType | null = typeSlug
    ? (onboardingTypeBySlug(typeSlug) ?? null)
    : null;
  const [submitted, setSubmitted] = useState<OnboardingRequest | null>(null);
  useEffect(() => setSubmitted(null), [typeSlug]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<OnboardingFilter>("open");
  const [editing, setEditing] = useState<OnboardingRequest | null>(null);
  const queryClient = useQueryClient();

  const requestsQuery = useQuery(
    "onboarding-requests",
    () => apiService.getOnboardingRequests(),
    {
      select: (res): OnboardingListData =>
        res.data?.success
          ? (res.data.data as OnboardingListData)
          : { items: [], isAdmin: false },
    },
  );

  const statusMutation = useMutation(
    ({ id, status }: { id: string; status: string }) =>
      apiService.updateOnboardingRequestStatus(id, status),
    {
      onSuccess: (_res, { status }) => {
        queryClient.invalidateQueries("onboarding-requests");
        toast.success(`Moved to ${featureRequestStatusLabel(status)}`);
      },
      onError: (error) => {
        toast.error(extractApiError(error));
      },
    },
  );

  const deleteMutation = useMutation(
    (id: string) => apiService.deleteOnboardingRequest(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("onboarding-requests");
        setSelectedId(null);
        toast.success("Request deleted");
      },
      onError: (error) => {
        toast.error(extractApiError(error));
      },
    },
  );

  const types = ONBOARDING_REQUEST_TYPES;
  const requests = requestsQuery.data?.items ?? [];
  const counts = countOnboardingRequests(requests);
  const visibleRequests = filterOnboardingRequests(requests, filter);
  const isAdmin = requestsQuery.data?.isAdmin ?? false;
  const typeLabel = (slug: string) =>
    types.find((t) => t.slug === slug)?.label ?? slug;
  const selectedRequest = requests.find((r) => r.id === selectedId) ?? null;

  const startNew = () => {
    setSubmitted(null);
    navigate("/onboarding/new");
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Onboarding
        </h1>
        <p className="mt-1 text-gray-600 dark:text-gray-300">
          Request access to platform services run by the DevX team. The team
          picks requests up from this page and updates their status here as they
          progress.
        </p>
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex gap-6" aria-label="Onboarding views">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() =>
                  tab.id === "new" ? startNew() : navigate("/onboarding")
                }
                aria-current={isActive ? "page" : undefined}
                className={`inline-flex items-center gap-2 border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                  isActive
                    ? "border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-200"
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === "new" && submitted && (
        <MessageCard icon={CheckCircle2} title="Request submitted">
          <p>
            <span className="font-medium">{submitted.title}</span> has been sent
            to the DevX team. Follow its status on the Requests tab; the team
            will contact the owner listed in the request if they need anything
            else.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate("/onboarding")}
            >
              View requests
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={startNew}
            >
              Submit another request
            </button>
          </div>
        </MessageCard>
      )}

      {activeTab === "new" && typeSlug && !selectedType && (
        <MessageCard icon={Rocket} title="Unknown service" tone="warning">
          There is no onboarding flow called "{typeSlug}".{" "}
          <button
            type="button"
            className="text-primary-600 underline dark:text-primary-400"
            onClick={() => navigate("/onboarding/new")}
          >
            Choose a service
          </button>
        </MessageCard>
      )}

      {activeTab === "new" && !submitted && !typeSlug && (
        <>
          <div className="grid gap-4 md:grid-cols-2">
            {types.map((type) => {
              const Icon = typeIcon(type.slug);
              return (
                <button
                  key={type.slug}
                  type="button"
                  onClick={() => navigate(`/onboarding/new/${type.slug}`)}
                  className="card w-full text-left transition-shadow hover:shadow-md"
                >
                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-primary-100 p-3 text-primary-700 dark:bg-primary-900 dark:text-primary-200">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        {type.label}
                      </h2>
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                        {type.description}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {activeTab === "new" && !submitted && selectedType && (
        <>
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {selectedType.label}
            </h2>
            <button
              type="button"
              className="text-sm text-primary-600 hover:underline dark:text-primary-400"
              onClick={() => navigate("/onboarding/new")}
            >
              Choose a different service
            </button>
          </div>
          <OnboardingRequestForm
            key={selectedType.slug}
            type={selectedType}
            onSuccess={(request) => setSubmitted(request)}
            onCancel={() => navigate("/onboarding/new")}
          />
        </>
      )}

      {activeTab === "requests" && editing && (
        <>
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Edit request: {editing.title}
            </h2>
            <button
              type="button"
              className="text-sm text-primary-600 hover:underline dark:text-primary-400"
              onClick={() => setEditing(null)}
            >
              Back to requests
            </button>
          </div>
          {onboardingTypeBySlug(editing.type) ? (
            <OnboardingRequestForm
              key={editing.id}
              type={onboardingTypeBySlug(editing.type)!}
              request={editing}
              onSuccess={() => {
                toast.success("Request updated");
                setEditing(null);
              }}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <MessageCard icon={ClipboardList} title="Unknown request type">
              This request cannot be edited from here.
            </MessageCard>
          )}
        </>
      )}

      {activeTab === "requests" && !editing && (
        <>
          {requestsQuery.isLoading && <LoadingSpinner />}
          {requestsQuery.isError && (
            <MessageCard
              icon={ClipboardList}
              title="Could not load requests"
              tone="danger"
            >
              Please try again in a moment.
            </MessageCard>
          )}
          {!requestsQuery.isLoading && !requestsQuery.isError && (
            <div
              role="group"
              aria-label="Filter requests"
              className="inline-flex rounded-lg border border-gray-200 bg-white p-1 dark:border-gray-700 dark:bg-gray-800"
            >
              {ONBOARDING_FILTERS.map((option) => {
                const isActive = filter === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setFilter(option.id)}
                    aria-pressed={isActive}
                    className={`rounded-md px-3 py-1 text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-primary-600 text-white dark:bg-primary-500"
                        : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                    }`}
                  >
                    {option.label}
                    <span
                      className={`ml-1.5 text-xs ${
                        isActive
                          ? "text-primary-100"
                          : "text-gray-400 dark:text-gray-500"
                      }`}
                    >
                      {counts[option.id]}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          {!requestsQuery.isLoading &&
            !requestsQuery.isError &&
            (visibleRequests.length === 0 ? (
              <MessageCard
                icon={Rocket}
                title={
                  requests.length === 0
                    ? "No onboarding requests yet"
                    : `No ${filter} requests`
                }
              >
                {requests.length === 0
                  ? "Use the New request tab to ask the DevX team for a Kubernetes namespace or DependencyTrack access."
                  : "Switch the filter above to see other requests."}
              </MessageCard>
            ) : (
              <div className="card overflow-x-auto p-0">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      {[
                        "Request",
                        "Service",
                        "Requested by",
                        "Status",
                        "Submitted",
                        "Handled by",
                        "Status changed",
                      ].map((heading) => (
                        <th
                          key={heading}
                          scope="col"
                          className="whitespace-nowrap px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                        >
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {visibleRequests.map((request) => (
                      <tr key={request.id}>
                        <td className="max-w-md px-3 py-3 text-sm">
                          <button
                            type="button"
                            onClick={() => setSelectedId(request.id)}
                            className="text-left"
                          >
                            {splitTitle(request.title).kind && (
                              <span className="block text-xs text-gray-500 dark:text-gray-400">
                                {splitTitle(request.title).kind}
                              </span>
                            )}
                            <span className="block font-medium text-primary-600 hover:underline dark:text-primary-400">
                              {splitTitle(request.title).subject}
                            </span>
                          </button>
                          {editedSinceStatusChange(request) && (
                            <span
                              title={`Edited ${new Date(request.editedAt ?? "").toLocaleString()} by ${request.editedByName || request.editedBy}`}
                              className="ml-2 inline-flex rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                            >
                              Edited
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-300">
                          {typeLabel(request.type)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-600 dark:text-gray-300">
                          {request.requesterName || request.requesterEmail}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-sm">
                          {isAdmin ? (
                            <select
                              aria-label={`Status for ${request.title}`}
                              value={request.status}
                              onChange={(e) =>
                                statusMutation.mutate({
                                  id: request.id,
                                  status: e.target.value,
                                })
                              }
                              disabled={statusMutation.isLoading}
                              className="input h-auto w-auto py-1 pr-8 text-sm"
                            >
                              {ONBOARDING_STATUSES.map((status) => (
                                <option key={status} value={status}>
                                  {featureRequestStatusLabel(status)}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <OnboardingStatusBadge status={request.status} />
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-600 dark:text-gray-300">
                          <DateTimeCell value={request.createdAt} />
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-600 dark:text-gray-300">
                          {request.statusChangedBy ? (
                            <span title={request.statusChangedBy}>
                              {request.statusChangedByName ||
                                request.statusChangedBy}
                            </span>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">
                              Unassigned
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-600 dark:text-gray-300">
                          <DateTimeCell value={request.statusChangedAt} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
        </>
      )}

      {selectedRequest && (
        <OnboardingRequestDetailModal
          request={selectedRequest}
          type={onboardingTypeBySlug(selectedRequest.type)}
          isAdmin={isAdmin}
          statusUpdating={statusMutation.isLoading}
          onStatusChange={(status) =>
            statusMutation.mutate({ id: selectedRequest.id, status })
          }
          onEdit={
            selectedRequest.canEdit
              ? () => {
                  setSelectedId(null);
                  setEditing(selectedRequest);
                }
              : undefined
          }
          onDelete={
            selectedRequest.canEdit
              ? () => {
                  if (
                    window.confirm(
                      `Delete "${selectedRequest.title}"? This cannot be undone.`,
                    )
                  ) {
                    deleteMutation.mutate(selectedRequest.id);
                  }
                }
              : undefined
          }
          deleting={deleteMutation.isLoading}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}
